import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { findKnownEmbed, isReloadableEmbed } from 'utils/embeds';
import ContentEmbedContext from 'context/contentEmbed';

// For more documentation on this see docs/article-content-embeds.md
const ContentEmbedProvider = ({ children }) => {
  const [loadedReloadableScripts, setLoadedReloadableScripts] = useState([]);
  const [currentScripts, setCurrentScripts] = useState([]);
  const previousScriptsRef = useRef([]);

  const addScriptEmbeds = (scriptSrcs) => {
    const knownReloadableEmbedsToAdd = scriptSrcs.filter(
      (src) => !loadedReloadableScripts.includes(src) && isReloadableEmbed(findKnownEmbed(src)),
    );
    if (0 < knownReloadableEmbedsToAdd.length) {
      setLoadedReloadableScripts((scripts) => ([...scripts, ...knownReloadableEmbedsToAdd]));
    }
    setCurrentScripts((scripts) => [...new Set([...scripts, ...scriptSrcs])]);
  };

  const removeScriptEmbeds = (scriptSrcs) => {
    setCurrentScripts((scripts) => scripts.filter((src) => !scriptSrcs.includes(src)));
  };

  useEffect(() => {
    const newScripts = currentScripts.filter(
      (script) => !previousScriptsRef.current.includes(script),
    );
    newScripts.forEach((scriptSrc) => {
      if (loadedReloadableScripts.includes(scriptSrc)) {
        const knownEmbed = findKnownEmbed(scriptSrc);
        if (knownEmbed.isReadyForManualProcessing()) {
          knownEmbed.processPageEmbeds();
        }
      }
    });
    const removedScripts = previousScriptsRef.current.filter(
      (script) => !currentScripts.includes(script),
    );
    removedScripts.forEach((scriptSrc) => {
      const embed = findKnownEmbed(scriptSrc);
      embed?.onUnload?.();
    });
    previousScriptsRef.current = currentScripts;
  }, [loadedReloadableScripts, currentScripts]);

  const allScripts = [...new Set([...loadedReloadableScripts, ...currentScripts])];

  return (<>
    <Helmet>
      {allScripts.map((src) => <script defer key={src} src={src} type="text/javascript" />)}
    </Helmet>
    <ContentEmbedContext.Provider value={{ addScriptEmbeds, removeScriptEmbeds }}>
      {children}
    </ContentEmbedContext.Provider>
  </>);
};

ContentEmbedProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ContentEmbedProvider;
